/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import Component from 'can/component/component';
import can from 'can';
import fill from 'lodash/fill';
import template from './scorecard.stache';

Component.extend({
    tag: 'leaderboard-scorecard',
    template: template,
    viewModel: {
        define: {
            player: {
                Type: can.Map,
                value: null,
            },
            showLegends: {
                type: 'boolean',
                value: true,
            },
            round: {
                type: 'number',
                value: 1,
            },
            selectedScorecard: {
                type: 'string',
                value: 'out',
            },
            availableRounds: {
                get() {
                    let rounds = [];
                    const round = this.attr('round');
                    const roundsInfo = this.attr('player.rounds');
                    if (round) {
                        let showLegends = true;
                        roundsInfo[round - 1].holes.forEach((hole) => {
                            if (!hole.par && showLegends) {
                                showLegends = false;
                            }
                        });
                        this.attr('showLegends', showLegends);
                        return [round];
                    }
                    if (roundsInfo) {
                        for (let i = 0; i < roundsInfo.length; i++) {
                            if (
                                roundsInfo[i].holes &&
                                roundsInfo[i].holes.length
                            ) {
                                rounds.push(i + 1);
                            }
                        }
                        return rounds;
                    }
                    return [];
                },
            },
        },

        incIndex(index, number) {
            return index + number;
        },

        makeScoresList(allHoles, isOut = true) {
            const startingHole = isOut ? 1 : 10;
            const holes = allHoles
                .filter((hole) => (isOut ? hole.number < 10 : hole.number > 9))
                .sort((hole1, hole2) => hole1.number - hole2.number);
            const rawHoles = holes.attr();

            const holesToAppend =
                rawHoles.length > 0
                    ? (isOut ? 9 : 18) -
                      Math.max(...rawHoles.map((hole) => hole.number))
                    : 9;
            const holesToPrepend = rawHoles.reduce((accum, hole, index) => {
                if (accum) {
                    return accum;
                }

                if (hole.number > startingHole + index) {
                    return hole.number - 1;
                }

                return accum;
            }, 0);

            if (holesToAppend) {
                holes.push(...fill(Array(holesToAppend), {}));
            }
            if (holesToPrepend) {
                holes.unshift(...fill(Array(holesToPrepend), {}));
            }

            return holes;
        },

        getScoresOut(round) {
            const roundsInfo = this.attr('player.rounds');
            if (
                round &&
                roundsInfo &&
                roundsInfo[round - 1] &&
                roundsInfo[round - 1].holes
            ) {
                return this.makeScoresList(roundsInfo[round - 1].holes);
            }
            return [];
        },

        getScoresIn(round) {
            const roundsInfo = this.attr('player.rounds');

            if (
                round &&
                roundsInfo &&
                roundsInfo[round - 1] &&
                roundsInfo[round - 1].holes
            ) {
                return this.makeScoresList(roundsInfo[round - 1].holes, false);
            }
            return [];
        },

        getRoundInfo(round) {
            const roundsInfo = this.attr('player.rounds');
            return roundsInfo ? roundsInfo[round - 1] : null;
        },

        getScoreColor(par, score) {
            if (!par) {
                return '';
            }
            score = parseInt(score) || 0;
            par = parseInt(par) || 0;
            const diff = score - par;

            switch (true) {
                case diff <= -2:
                    return 'eagle';
                case diff === -1:
                    return 'birdie';
                case diff === 0:
                    return 'par';
                case diff === 1:
                    return 'bogey';
                case diff >= 2:
                    return 'double-bogey';
            }
        },
    },
    events: {
        '.s-switcher-btn click'($el) {
            this.viewModel.attr('selectedScorecard', $el.data('tab'));
        },
    },
});
